/**
 * La foto del trabajador en su ficha del ERP: verla, subirla, cambiarla, quitarla.
 *
 * La sube RRHH y la ve **toda la organización** en el portal y el organigrama
 * (plan del portal, D4). Por eso el aviso está a la vista al subirla: es un dato
 * personal (Ley 21.719) y quien la sube tiene que saber dónde va a aparecer.
 *
 * El servidor la reprocesa siempre —cuadrada, 512 px, sin metadatos—, así que acá
 * no se recorta ni se valida más que el tipo: la regla vive en un solo lugar.
 */
import { useRef } from 'react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/ds/Avatar'
import { usePermission } from '@/hooks/usePermission'
import { emitToast } from '@/lib/globalToast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { useRemoveEmployeePhoto, useUploadEmployeePhoto } from '../hooks/usePayroll'

interface Props {
  employeeId: string
  nombre: string
  fotoUrl?: string | null
}

export function EmployeePhotoField({ employeeId, nombre, fotoUrl }: Props) {
  const { can } = usePermission()
  const puedeEditar = can('payroll.employees', 'edit')
  const input = useRef<HTMLInputElement>(null)
  const subir = useUploadEmployeePhoto(employeeId)
  const quitar = useRemoveEmployeePhoto(employeeId)
  const ocupado = subir.isPending || quitar.isPending

  const alElegir = (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0]
    // Se limpia antes de subir: si no, elegir otra vez el mismo archivo tras un
    // error no dispara `change` y parece que el botón no hace nada.
    e.target.value = ''
    if (!archivo) return
    subir.mutate(archivo, {
      onSuccess: () => emitToast('success', 'Foto actualizada.'),
      onError: (err) => emitToast('error', extractErrorMessage(err, 'No se pudo subir la foto.')),
    })
  }

  return (
    <div className="flex items-center gap-4">
      <Avatar nombre={nombre} fotoUrl={fotoUrl} tamano="lg" />
      {puedeEditar && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <input
              ref={input}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              aria-label={fotoUrl ? 'Cambiar la foto' : 'Subir una foto'}
              onChange={alElegir}
            />
            <button
              type="button"
              onClick={() => input.current?.click()}
              disabled={ocupado}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-divider text-content hover:bg-soft disabled:opacity-50"
            >
              {subir.isPending ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
              {fotoUrl ? 'Cambiar foto' : 'Subir foto'}
            </button>
            {fotoUrl && (
              <button
                type="button"
                onClick={() => quitar.mutate(undefined, {
                  onError: (err) => emitToast('error', extractErrorMessage(err, 'No se pudo quitar la foto.')),
                })}
                disabled={ocupado}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-content-muted hover:text-danger-content hover:bg-danger-surface disabled:opacity-50"
              >
                <Trash2 size={14} /> Quitar
              </button>
            )}
          </div>
          <p className="text-xs text-content-muted max-w-sm">
            La foto es visible para toda la organización en el portal del colaborador y el
            organigrama. JPG, PNG o WebP, hasta 8 MB.
          </p>
        </div>
      )}
    </div>
  )
}
